import type { Dispatch, SetStateAction } from 'react'
import type { IClientUser } from '../../interfaces/user';
import React, {
  Fragment,
  useContext,
  useState
} from 'react';

import { CommentCategory } from '../../components/Comment/constants';
import Navigation from '../../components/Navigation';
import { ProjectFeed } from '../../components/Project/ProjectFeed';
import UserCard from '../../components/User/UserCard';
import UserProfileModal from '../../components/User/UserProfileModal';
import {
  CurrentUserContext,
  CurrentUserContextProvider
} from '../../contexts/currentUserContext';
import { MessageContextProvider } from '../../contexts/messageContext';
import { UserContext, UserContextProvider } from '../../contexts/userContext';
import {
  UserProjectsContext,
  UserProjectsContextProvider
} from '../../contexts/userProjectsContext';
import { FollowContextProvider } from '../../contexts/followContext';
import { PortfolioContextProvider } from '../../contexts/portfolioContext';
import PortfolioGrid from '../../components/Portfolio/PortfolioGrid';
import ProfileMainTabs, { ProfileMainTabType } from '../../components/User/ProfileMainTabs';

export interface IUserProfile extends IClientUser {
  match: {
    params: {
      userId?: string;
    };
  };
}

const UserContent = () => {
  const user = useContext(UserContext);
  const [mainTab, setMainTab] = useState<ProfileMainTabType>(ProfileMainTabType.PROJECT);

  return (
    <Fragment>
      <ProfileMainTabs
        activeTab={mainTab}
        setActiveTab={setMainTab}
      />

      {mainTab === ProfileMainTabType.PROJECT && (
        <UserProjects />
      )}

      {mainTab === ProfileMainTabType.PORTFOLIO && (
        <PortfolioContextProvider userId={user.id}>
          <PortfolioGrid isOwner={false} />
        </PortfolioContextProvider>
      )}
    </Fragment>
  );
};

const UserProjects = () => {
  const currentUser = useContext(CurrentUserContext);
  const projects = useContext(UserProjectsContext);
  return (
    <Fragment>
      {projects && projects.length > 0 ? (
        projects.map((project, idx) => (
          <ProjectFeed
            key={idx}
            project={project}
            user={currentUser}
            commentFrom={CommentCategory.userProjectListing}
          />
        ))
      ) : (
        <div className="notification is-warning">
          <strong>No available projects yet.</strong>
        </div>
      )}
    </Fragment>
  );
};

const ShowUserCard = ({
  setIsOpen
}: {
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const currentUser = useContext(CurrentUserContext);
  const user = useContext(UserContext);

  return (
    <MessageContextProvider currentUser={currentUser}>
      <FollowContextProvider>
        <UserCard
          currentUser={currentUser}
          user={user}
          setIsUserProfileModal={setIsOpen}
        />
      </FollowContextProvider>
    </MessageContextProvider>
  );
};

const UserShow = (props: IUserProfile) => {
  const [modalIsOpen, setIsOpen] = useState(false);
  const handleModalToggle = () => setIsOpen(!modalIsOpen);

  return (
    <main className="page-project-list">
      <Navigation {...props} />
      <section className="container">
        <div className="columns section">
          <div className="column">
            <CurrentUserContextProvider>
              <UserContextProvider userId={props.match.params.userId as any}>
                <ShowUserCard setIsOpen={setIsOpen} />
                <UserProfileModal
                  handleModalToggle={handleModalToggle}
                  modalIsOpen={modalIsOpen}
                />
              </UserContextProvider>
            </CurrentUserContextProvider>
          </div>
          <div className="column is-three-quarters">
            <div className="container">
              <CurrentUserContextProvider>
                <UserContextProvider userId={props.match.params.userId as any}>
                  <UserProjectsContextProvider userId={props.match.params.userId}>
                    <UserContent />
                  </UserProjectsContextProvider>
                </UserContextProvider>
              </CurrentUserContextProvider>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default UserShow;
